import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { FIRM } from "@/data/content";
import { SERVICE_GROUPS } from "@/data/servicesIA";

export default function Footer() {
  return (
    <footer className="brand-panel-dark" data-testid="site-footer">
      {/* Footer CTA */}
      <div className="border-b border-white/10">
        <div className="brand-container py-20 lg:py-24">
          <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
            <div>
              <p className="brand-eyebrow-on-dark">
                Speak to our award winning legal team
              </p>
              <h2 className="mt-4 text-4xl font-light tracking-tight sm:text-5xl">
                Let us take it from here.
              </h2>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <a
                href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
                className="brand-link-on-dark flex items-center gap-3 text-lg"
                data-testid="footer-emergency-phone"
              >
                <Phone className="size-5 text-brand-terracotta" />
                <span>
                  <span className="mr-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                    Office
                  </span>
                  {FIRM.phone}
                </span>
              </a>
              <Link
                to="/contact"
                className="brand-cta-primary px-8 py-4 text-center tracking-[0.2em]"
                data-testid="footer-cta-button"
              >
                Get in touch
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer nav */}
      <div className="brand-container py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="text-lg font-semibold tracking-[0.18em]">HALLINANS</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              {FIRM.fullName}. Operating since the 1950s, specialists in defending
              serious and complex crime and fraud.
            </p>
            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-brand-gold">
              {FIRM.accreditation}
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Services
            </p>
            <ul className="mt-4 space-y-2.5">
              {SERVICE_GROUPS.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/services/${g.slug}`}
                    className="brand-link-on-dark text-sm"
                    data-testid={`footer-service-${g.slug}`}
                  >
                    {g.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/how-we-help"
                  className="brand-link-on-dark text-sm"
                  data-testid="footer-how-we-help-link"
                >
                  How we help — by situation
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Firm
            </p>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/about" className="brand-link-on-dark text-sm" data-testid="footer-about-link">About us</Link></li>
              <li><Link to="/team" className="brand-link-on-dark text-sm" data-testid="footer-team-link">Meet the team</Link></li>
              <li><Link to="/results" className="brand-link-on-dark text-sm" data-testid="footer-results-link">Our results</Link></li>
              <li><Link to="/fees-funding" className="brand-link-on-dark text-sm" data-testid="footer-fees-link">Fees & funding</Link></li>
              <li><Link to="/accreditations" className="brand-link-on-dark text-sm" data-testid="footer-accreditations-link">Accreditations</Link></li>
              <li><Link to="/resources" className="brand-link-on-dark text-sm" data-testid="footer-resources-link">Legal resources</Link></li>
              <li><Link to="/resources/news" className="brand-link-on-dark text-sm" data-testid="footer-news-link">Hallinans news</Link></li>
              <li><Link to="/contact" className="brand-link-on-dark text-sm" data-testid="footer-contact-link">Contact us</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Visit us
            </p>
            <address className="mt-4 space-y-2.5 not-italic">
              <p className="flex items-start gap-2.5 text-sm text-slate-300">
                <MapPin className="mt-0.5 size-4 shrink-0 text-brand-gold" />
                <span>{FIRM.address.join(", ")}</span>
              </p>
              <p className="flex items-center gap-2.5 text-sm text-slate-300">
                <Phone className="size-4 shrink-0 text-brand-gold" />
                <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-brand-gold" data-testid="footer-office-phone">
                  {FIRM.phone}
                </a>
              </p>
              <p className="flex items-center gap-2.5 text-sm text-slate-300">
                <Mail className="size-4 shrink-0 text-brand-gold" />
                <a href={`mailto:${FIRM.email}`} className="transition-colors hover:text-brand-gold" data-testid="footer-email">
                  {FIRM.email}
                </a>
              </p>
              <p className="flex items-center gap-2.5 text-sm text-slate-300">
                <Clock className="size-4 shrink-0 text-brand-gold" />
                {FIRM.hours}
              </p>
            </address>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="brand-container flex flex-col gap-2 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} {FIRM.fullName}. All rights reserved.</p>
          <p>
            Authorised and regulated by the Solicitors Regulation Authority.{" "}
            <Link to="/legal-regulatory" className="underline transition-colors hover:text-brand-gold" data-testid="footer-regulatory-link">
              Legal & regulatory
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
